import { CalendarOutlined, DownloadOutlined } from "@ant-design/icons";
import { Button, Modal, Radio, Select, Space, Switch, Typography } from "antd";
import type { RadioChangeEvent } from "antd";
import { saveAs } from "file-saver";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useExportBusinessAssetsMutation } from "../../../../../../../api/graphql";
import { getTaxYearOptions } from "../../../../../TaxDeclaration/hooks/useTaxYearParam";
import {
  foundationColors,
  systemColors,
} from "../../../../../../../themes/colors";
import {
  buildExportFileName,
  showErrorNotification,
} from "../../../../../../../utils";
import { useKontaxUserContext } from "../../../../../../contexts/KontaxUserContext";

type ExportBusinessAssetsModalProps = {
  open: boolean;
  onClose: () => void;
  email: string;
  taxYear: number;
};

enum ReportType {
  DETAILED = "DETAILED",
  SUMMARY = "SUMMARY",
}

enum FileFormat {
  CSV = "CSV",
  PDF = "PDF",
}

const defaultRadioCardStyle = {
  width: "100%",
  border: `2px solid ${foundationColors["deep-purple-200"]}`,
  borderRadius: 8,
  background: foundationColors["lavender-100"],
  padding: "10px 12px",
  display: "flex",
  alignItems: "flex-start",
} as const;

const subtitleStyle = { color: foundationColors["deep-purple-500"] } as const;

type FileFormatButtonProps = {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
};

const FileFormatButton = ({
  label,
  selected,
  disabled = false,
  onClick,
}: FileFormatButtonProps) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    style={{
      flex: 1,
      border: "none",
      borderRadius: 6,
      background: disabled
        ? foundationColors["lavender-100"]
        : selected
        ? foundationColors["base-deep-purple"]
        : "transparent",
      color: disabled
        ? foundationColors["deep-purple-300"]
        : selected
        ? foundationColors.white
        : foundationColors["base-deep-purple"],
      padding: "10px 18px",
      fontWeight: 700,
      fontSize: 16,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {label}
  </button>
);

const ExportBusinessAssetsModal = ({
  open,
  onClose,
  email,
  taxYear,
}: ExportBusinessAssetsModalProps) => {
  const { t } = useTranslation("BusinessAssetExport");
  const [reportType, setReportType] = useState<ReportType>(ReportType.DETAILED);
  const [fileFormat, setFileFormat] = useState<FileFormat>(FileFormat.PDF);
  const [selectedYear, setSelectedYear] = useState(taxYear);
  const [withReceipts, setWithReceipts] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportBusinessAssets] = useExportBusinessAssetsMutation();
  const { user } = useKontaxUserContext();
  const isWithReceiptsEnabled = fileFormat === FileFormat.PDF;

  useEffect(() => {
    setSelectedYear(taxYear);
  }, [taxYear, open]);

  useEffect(() => {
    if (!isWithReceiptsEnabled) {
      setWithReceipts(false);
    }
  }, [isWithReceiptsEnabled]);

  const reportTypeOptions = useMemo(
    () =>
      [
        {
          value: ReportType.DETAILED,
          title: t("reportDetailedTitle"),
          subtitle: t("reportDetailedSubtitle"),
        },
        {
          value: ReportType.SUMMARY,
          title: t("reportSummaryTitle"),
          subtitle: t("reportSummarySubtitle"),
        },
      ] as const,
    [t]
  );

  const onReportTypeChange = (event: RadioChangeEvent) => {
    setReportType(event.target.value as ReportType);
  };

  const downloadArchive = (base64Archive: string, fileName: string) => {
    const bytes = atob(base64Archive);
    const buffer = new Uint8Array(bytes.length);

    for (let index = 0; index < bytes.length; index += 1) {
      buffer[index] = bytes.charCodeAt(index);
    }

    const archiveBlob = new Blob([buffer], { type: "application/zip" });
    saveAs(archiveBlob, fileName);
  };

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const { data } = await exportBusinessAssets({
        variables: {
          email,
          reportType,
          fileFormat,
          year: selectedYear,
          withReceipts: isWithReceiptsEnabled ? withReceipts : undefined,
        },
      });

      const payload = data?.exportBusinessAssets;
      if (!payload?.archive) {
        throw new Error("No archive was returned by exportBusinessAssets");
      }

      downloadArchive(
        payload.archive,
        payload.archiveName ||
          payload.fileName ||
          buildExportFileName({
            // The context keeps the previous customer while it refetches.
            user: user && user.email === email ? user : { email },
            exportType: "Anlagenverzeichnis",
            period: { kind: "year", year: selectedYear },
            extension: "zip",
          })
      );
      onClose();
    } catch (error) {
      showErrorNotification({
        message: `An error occurred while exporting business assets: ${
          (error as Error).message
        }`,
      });
    } finally {
      setIsExporting(false);
    }
  };
  const taxYearOptions = useMemo(
    () =>
      getTaxYearOptions()
        .reverse()
        .map((year) => ({ label: year, value: year })),
    []
  );

  return (
    <Modal
      title={null}
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={600}
    >
      <Typography.Title level={3}>{t("modalTitle")}</Typography.Title>

      <Space direction="vertical" size={10} style={{ width: "100%" }}>
        <Typography.Text strong>{t("reportType")}</Typography.Text>
        <Radio.Group
          value={reportType}
          onChange={onReportTypeChange}
          style={{ width: "100%" }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            {reportTypeOptions.map((option) => (
              <Radio
                key={option.value}
                value={option.value}
                style={{
                  ...defaultRadioCardStyle,
                  border:
                    reportType === option.value
                      ? `2px solid ${systemColors["semantic-red-500"]}`
                      : defaultRadioCardStyle.border,
                  background:
                    reportType === option.value
                      ? systemColors["semantic-red-100"]
                      : defaultRadioCardStyle.background,
                }}
              >
                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                  <Typography.Text strong>{option.title}</Typography.Text>
                  <Typography.Text style={subtitleStyle}>
                    {option.subtitle}
                  </Typography.Text>
                </Space>
              </Radio>
            ))}
          </Space>
        </Radio.Group>

        <Space
          direction="vertical"
          size={8}
          style={{ width: "100%", marginTop: 20 }}
        >
          <Typography.Text strong>{t("fiscalYear")}</Typography.Text>
          <div style={{ ...defaultRadioCardStyle, width: "25%" }}>
            <Space size={8} align="center">
              <CalendarOutlined />
              <Select
                value={selectedYear}
                onChange={setSelectedYear}
                options={taxYearOptions}
                bordered={false}
                style={{ minWidth: 90 }}
              />
            </Space>
          </div>
        </Space>

        <Space
          direction="vertical"
          size={8}
          style={{ width: "100%", marginTop: 20 }}
        >
          <Space direction="vertical" size={8} style={{ width: "100%" }}>
            <Typography.Text strong>{t("fileFormat")}</Typography.Text>
            <div style={{ ...defaultRadioCardStyle, width: "25%", padding: 0 }}>
              <Space size={0} style={{ width: "100%" }}>
                <FileFormatButton
                  label={t("fileFormatCsv")}
                  selected={fileFormat === FileFormat.CSV}
                  onClick={() => setFileFormat(FileFormat.CSV)}
                />
                <FileFormatButton
                  label={t("fileFormatPdf")}
                  selected={fileFormat === FileFormat.PDF}
                  onClick={() => setFileFormat(FileFormat.PDF)}
                />
              </Space>
            </div>
          </Space>
        </Space>
        <Space
          direction="vertical"
          size={8}
          style={{
            width: "100%",
            marginTop: 20,
            opacity: isWithReceiptsEnabled ? 1 : 0.6,
            cursor: isWithReceiptsEnabled ? "default" : "not-allowed",
          }}
        >
          <Typography.Text
            strong
            style={{
              color: isWithReceiptsEnabled
                ? foundationColors["base-deep-purple"]
                : foundationColors["deep-purple-400"],
            }}
          >
            {t("withReceiptsTitle")}
          </Typography.Text>
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <Space size={8} align="center">
              <Switch
                checked={withReceipts}
                disabled={!isWithReceiptsEnabled}
                onChange={setWithReceipts}
                style={
                  withReceipts
                    ? { backgroundColor: foundationColors["base-deep-purple"] }
                    : undefined
                }
              />
              <Typography.Text
                style={{
                  paddingLeft: 8,
                  color: isWithReceiptsEnabled
                    ? foundationColors["base-deep-purple"]
                    : foundationColors["deep-purple-400"],
                }}
              >
                {withReceipts ? t("commonYes") : t("commonNo")}
              </Typography.Text>
            </Space>
            <Typography.Text
              style={{
                color: isWithReceiptsEnabled
                  ? subtitleStyle.color
                  : foundationColors["deep-purple-400"],
              }}
            >
              {t("withReceiptsSubtitle")}
            </Typography.Text>
          </Space>
        </Space>
        <div
          style={{
            width: "100%",
            height: 2,
            marginTop: 20,
            background: foundationColors["deep-purple-200"],
          }}
        />
        <div
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 16,
          }}
        >
          <Space>
            <Button
              onClick={onClose}
              style={{
                borderRadius: 8,
                borderColor: foundationColors["base-deep-purple"],
                color: foundationColors["base-deep-purple"],
                background: foundationColors.white,
              }}
            >
              {t("cancel")}
            </Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              loading={isExporting}
              onClick={handleExport}
              style={{
                borderRadius: 8,
                background: foundationColors["base-deep-purple"],
                borderColor: foundationColors["base-deep-purple"],
              }}
            >
              {t("export")}
            </Button>
          </Space>
        </div>
      </Space>
    </Modal>
  );
};

export default ExportBusinessAssetsModal;
